/**
 * Location data for structured e-signature and waitlist capture.
 *
 * Goals:
 *   - Replace free-text country/state fields with validated dropdown input
 *     so the Mongo record contains canonical ISO codes instead of
 *     "Your Face" / "Your Butt" garbage.
 *   - Keep the module small: full country list (ISO 3166-1 alpha-2), with
 *     subdivision lists (ISO 3166-2) only for the common-law-aligned
 *     jurisdictions most relevant to governing-law analysis (US, CA, GB, AU).
 *   - For all other countries, the subdivision field accepts a validated
 *     text input (letters/spaces/hyphens, 1-60 chars) so we still capture a
 *     region but don't ship a 5000-row data file.
 *
 * Audit trail stored:
 *   {
 *     countryCode: "US",
 *     countryName: "United States",
 *     subdivisionCode: "US-WA" | null,  // ISO 3166-2 code when available
 *     subdivisionName: "Washington",    // Display name
 *     subdivisionInputType: "dropdown" | "text"
 *   }
 */

export interface Country {
  code: string;   // ISO 3166-1 alpha-2
  name: string;
}

export interface Subdivision {
  code: string;   // ISO 3166-2
  name: string;
}

/**
 * ISO 3166-1 alpha-2 country list.
 * Trimmed to the 249 assigned codes. Display names use commonly-used
 * short forms, not the UN full legal names.
 */
export const COUNTRIES: Country[] = [
  { code: "AF", name: "Afghanistan" },
  { code: "AX", name: "Åland Islands" },
  { code: "AL", name: "Albania" },
  { code: "DZ", name: "Algeria" },
  { code: "AS", name: "American Samoa" },
  { code: "AD", name: "Andorra" },
  { code: "AO", name: "Angola" },
  { code: "AI", name: "Anguilla" },
  { code: "AQ", name: "Antarctica" },
  { code: "AG", name: "Antigua and Barbuda" },
  { code: "AR", name: "Argentina" },
  { code: "AM", name: "Armenia" },
  { code: "AW", name: "Aruba" },
  { code: "AU", name: "Australia" },
  { code: "AT", name: "Austria" },
  { code: "AZ", name: "Azerbaijan" },
  { code: "BS", name: "Bahamas" },
  { code: "BH", name: "Bahrain" },
  { code: "BD", name: "Bangladesh" },
  { code: "BB", name: "Barbados" },
  { code: "BY", name: "Belarus" },
  { code: "BE", name: "Belgium" },
  { code: "BZ", name: "Belize" },
  { code: "BJ", name: "Benin" },
  { code: "BM", name: "Bermuda" },
  { code: "BT", name: "Bhutan" },
  { code: "BO", name: "Bolivia" },
  { code: "BQ", name: "Bonaire, Sint Eustatius and Saba" },
  { code: "BA", name: "Bosnia and Herzegovina" },
  { code: "BW", name: "Botswana" },
  { code: "BV", name: "Bouvet Island" },
  { code: "BR", name: "Brazil" },
  { code: "IO", name: "British Indian Ocean Territory" },
  { code: "BN", name: "Brunei Darussalam" },
  { code: "BG", name: "Bulgaria" },
  { code: "BF", name: "Burkina Faso" },
  { code: "BI", name: "Burundi" },
  { code: "CV", name: "Cabo Verde" },
  { code: "KH", name: "Cambodia" },
  { code: "CM", name: "Cameroon" },
  { code: "CA", name: "Canada" },
  { code: "KY", name: "Cayman Islands" },
  { code: "CF", name: "Central African Republic" },
  { code: "TD", name: "Chad" },
  { code: "CL", name: "Chile" },
  { code: "CN", name: "China" },
  { code: "CX", name: "Christmas Island" },
  { code: "CC", name: "Cocos (Keeling) Islands" },
  { code: "CO", name: "Colombia" },
  { code: "KM", name: "Comoros" },
  { code: "CG", name: "Congo" },
  { code: "CD", name: "Congo (Democratic Republic)" },
  { code: "CK", name: "Cook Islands" },
  { code: "CR", name: "Costa Rica" },
  { code: "CI", name: "Côte d'Ivoire" },
  { code: "HR", name: "Croatia" },
  { code: "CU", name: "Cuba" },
  { code: "CW", name: "Curaçao" },
  { code: "CY", name: "Cyprus" },
  { code: "CZ", name: "Czechia" },
  { code: "DK", name: "Denmark" },
  { code: "DJ", name: "Djibouti" },
  { code: "DM", name: "Dominica" },
  { code: "DO", name: "Dominican Republic" },
  { code: "EC", name: "Ecuador" },
  { code: "EG", name: "Egypt" },
  { code: "SV", name: "El Salvador" },
  { code: "GQ", name: "Equatorial Guinea" },
  { code: "ER", name: "Eritrea" },
  { code: "EE", name: "Estonia" },
  { code: "SZ", name: "Eswatini" },
  { code: "ET", name: "Ethiopia" },
  { code: "FK", name: "Falkland Islands" },
  { code: "FO", name: "Faroe Islands" },
  { code: "FJ", name: "Fiji" },
  { code: "FI", name: "Finland" },
  { code: "FR", name: "France" },
  { code: "GF", name: "French Guiana" },
  { code: "PF", name: "French Polynesia" },
  { code: "TF", name: "French Southern Territories" },
  { code: "GA", name: "Gabon" },
  { code: "GM", name: "Gambia" },
  { code: "GE", name: "Georgia" },
  { code: "DE", name: "Germany" },
  { code: "GH", name: "Ghana" },
  { code: "GI", name: "Gibraltar" },
  { code: "GR", name: "Greece" },
  { code: "GL", name: "Greenland" },
  { code: "GD", name: "Grenada" },
  { code: "GP", name: "Guadeloupe" },
  { code: "GU", name: "Guam" },
  { code: "GT", name: "Guatemala" },
  { code: "GG", name: "Guernsey" },
  { code: "GN", name: "Guinea" },
  { code: "GW", name: "Guinea-Bissau" },
  { code: "GY", name: "Guyana" },
  { code: "HT", name: "Haiti" },
  { code: "HM", name: "Heard Island and McDonald Islands" },
  { code: "VA", name: "Holy See (Vatican City)" },
  { code: "HN", name: "Honduras" },
  { code: "HK", name: "Hong Kong" },
  { code: "HU", name: "Hungary" },
  { code: "IS", name: "Iceland" },
  { code: "IN", name: "India" },
  { code: "ID", name: "Indonesia" },
  { code: "IR", name: "Iran" },
  { code: "IQ", name: "Iraq" },
  { code: "IE", name: "Ireland" },
  { code: "IM", name: "Isle of Man" },
  { code: "IL", name: "Israel" },
  { code: "IT", name: "Italy" },
  { code: "JM", name: "Jamaica" },
  { code: "JP", name: "Japan" },
  { code: "JE", name: "Jersey" },
  { code: "JO", name: "Jordan" },
  { code: "KZ", name: "Kazakhstan" },
  { code: "KE", name: "Kenya" },
  { code: "KI", name: "Kiribati" },
  { code: "KP", name: "Korea (North)" },
  { code: "KR", name: "Korea (South)" },
  { code: "KW", name: "Kuwait" },
  { code: "KG", name: "Kyrgyzstan" },
  { code: "LA", name: "Laos" },
  { code: "LV", name: "Latvia" },
  { code: "LB", name: "Lebanon" },
  { code: "LS", name: "Lesotho" },
  { code: "LR", name: "Liberia" },
  { code: "LY", name: "Libya" },
  { code: "LI", name: "Liechtenstein" },
  { code: "LT", name: "Lithuania" },
  { code: "LU", name: "Luxembourg" },
  { code: "MO", name: "Macao" },
  { code: "MG", name: "Madagascar" },
  { code: "MW", name: "Malawi" },
  { code: "MY", name: "Malaysia" },
  { code: "MV", name: "Maldives" },
  { code: "ML", name: "Mali" },
  { code: "MT", name: "Malta" },
  { code: "MH", name: "Marshall Islands" },
  { code: "MQ", name: "Martinique" },
  { code: "MR", name: "Mauritania" },
  { code: "MU", name: "Mauritius" },
  { code: "YT", name: "Mayotte" },
  { code: "MX", name: "Mexico" },
  { code: "FM", name: "Micronesia" },
  { code: "MD", name: "Moldova" },
  { code: "MC", name: "Monaco" },
  { code: "MN", name: "Mongolia" },
  { code: "ME", name: "Montenegro" },
  { code: "MS", name: "Montserrat" },
  { code: "MA", name: "Morocco" },
  { code: "MZ", name: "Mozambique" },
  { code: "MM", name: "Myanmar" },
  { code: "NA", name: "Namibia" },
  { code: "NR", name: "Nauru" },
  { code: "NP", name: "Nepal" },
  { code: "NL", name: "Netherlands" },
  { code: "NC", name: "New Caledonia" },
  { code: "NZ", name: "New Zealand" },
  { code: "NI", name: "Nicaragua" },
  { code: "NE", name: "Niger" },
  { code: "NG", name: "Nigeria" },
  { code: "NU", name: "Niue" },
  { code: "NF", name: "Norfolk Island" },
  { code: "MK", name: "North Macedonia" },
  { code: "MP", name: "Northern Mariana Islands" },
  { code: "NO", name: "Norway" },
  { code: "OM", name: "Oman" },
  { code: "PK", name: "Pakistan" },
  { code: "PW", name: "Palau" },
  { code: "PS", name: "Palestine" },
  { code: "PA", name: "Panama" },
  { code: "PG", name: "Papua New Guinea" },
  { code: "PY", name: "Paraguay" },
  { code: "PE", name: "Peru" },
  { code: "PH", name: "Philippines" },
  { code: "PN", name: "Pitcairn" },
  { code: "PL", name: "Poland" },
  { code: "PT", name: "Portugal" },
  { code: "PR", name: "Puerto Rico" },
  { code: "QA", name: "Qatar" },
  { code: "RE", name: "Réunion" },
  { code: "RO", name: "Romania" },
  { code: "RU", name: "Russia" },
  { code: "RW", name: "Rwanda" },
  { code: "BL", name: "Saint Barthélemy" },
  { code: "SH", name: "Saint Helena" },
  { code: "KN", name: "Saint Kitts and Nevis" },
  { code: "LC", name: "Saint Lucia" },
  { code: "MF", name: "Saint Martin (French)" },
  { code: "PM", name: "Saint Pierre and Miquelon" },
  { code: "VC", name: "Saint Vincent and the Grenadines" },
  { code: "WS", name: "Samoa" },
  { code: "SM", name: "San Marino" },
  { code: "ST", name: "São Tomé and Príncipe" },
  { code: "SA", name: "Saudi Arabia" },
  { code: "SN", name: "Senegal" },
  { code: "RS", name: "Serbia" },
  { code: "SC", name: "Seychelles" },
  { code: "SL", name: "Sierra Leone" },
  { code: "SG", name: "Singapore" },
  { code: "SX", name: "Sint Maarten (Dutch)" },
  { code: "SK", name: "Slovakia" },
  { code: "SI", name: "Slovenia" },
  { code: "SB", name: "Solomon Islands" },
  { code: "SO", name: "Somalia" },
  { code: "ZA", name: "South Africa" },
  { code: "GS", name: "South Georgia and the South Sandwich Islands" },
  { code: "SS", name: "South Sudan" },
  { code: "ES", name: "Spain" },
  { code: "LK", name: "Sri Lanka" },
  { code: "SD", name: "Sudan" },
  { code: "SR", name: "Suriname" },
  { code: "SJ", name: "Svalbard and Jan Mayen" },
  { code: "SE", name: "Sweden" },
  { code: "CH", name: "Switzerland" },
  { code: "SY", name: "Syria" },
  { code: "TW", name: "Taiwan" },
  { code: "TJ", name: "Tajikistan" },
  { code: "TZ", name: "Tanzania" },
  { code: "TH", name: "Thailand" },
  { code: "TL", name: "Timor-Leste" },
  { code: "TG", name: "Togo" },
  { code: "TK", name: "Tokelau" },
  { code: "TO", name: "Tonga" },
  { code: "TT", name: "Trinidad and Tobago" },
  { code: "TN", name: "Tunisia" },
  { code: "TR", name: "Türkiye" },
  { code: "TM", name: "Turkmenistan" },
  { code: "TC", name: "Turks and Caicos Islands" },
  { code: "TV", name: "Tuvalu" },
  { code: "UG", name: "Uganda" },
  { code: "UA", name: "Ukraine" },
  { code: "AE", name: "United Arab Emirates" },
  { code: "GB", name: "United Kingdom" },
  { code: "US", name: "United States" },
  { code: "UM", name: "United States Minor Outlying Islands" },
  { code: "UY", name: "Uruguay" },
  { code: "UZ", name: "Uzbekistan" },
  { code: "VU", name: "Vanuatu" },
  { code: "VE", name: "Venezuela" },
  { code: "VN", name: "Vietnam" },
  { code: "VG", name: "Virgin Islands (British)" },
  { code: "VI", name: "Virgin Islands (U.S.)" },
  { code: "WF", name: "Wallis and Futuna" },
  { code: "EH", name: "Western Sahara" },
  { code: "YE", name: "Yemen" },
  { code: "ZM", name: "Zambia" },
  { code: "ZW", name: "Zimbabwe" },
];

/**
 * ISO 3166-2 subdivisions for common-law jurisdictions.
 * Keys are ISO 3166-1 alpha-2 country codes.
 * Values are subdivision lists with ISO 3166-2 codes (country-prefixed).
 */
export const SUBDIVISIONS: Record<string, Subdivision[]> = {
  US: [
    { code: "US-AL", name: "Alabama" },
    { code: "US-AK", name: "Alaska" },
    { code: "US-AZ", name: "Arizona" },
    { code: "US-AR", name: "Arkansas" },
    { code: "US-CA", name: "California" },
    { code: "US-CO", name: "Colorado" },
    { code: "US-CT", name: "Connecticut" },
    { code: "US-DE", name: "Delaware" },
    { code: "US-DC", name: "District of Columbia" },
    { code: "US-FL", name: "Florida" },
    { code: "US-GA", name: "Georgia" },
    { code: "US-HI", name: "Hawaii" },
    { code: "US-ID", name: "Idaho" },
    { code: "US-IL", name: "Illinois" },
    { code: "US-IN", name: "Indiana" },
    { code: "US-IA", name: "Iowa" },
    { code: "US-KS", name: "Kansas" },
    { code: "US-KY", name: "Kentucky" },
    { code: "US-LA", name: "Louisiana" },
    { code: "US-ME", name: "Maine" },
    { code: "US-MD", name: "Maryland" },
    { code: "US-MA", name: "Massachusetts" },
    { code: "US-MI", name: "Michigan" },
    { code: "US-MN", name: "Minnesota" },
    { code: "US-MS", name: "Mississippi" },
    { code: "US-MO", name: "Missouri" },
    { code: "US-MT", name: "Montana" },
    { code: "US-NE", name: "Nebraska" },
    { code: "US-NV", name: "Nevada" },
    { code: "US-NH", name: "New Hampshire" },
    { code: "US-NJ", name: "New Jersey" },
    { code: "US-NM", name: "New Mexico" },
    { code: "US-NY", name: "New York" },
    { code: "US-NC", name: "North Carolina" },
    { code: "US-ND", name: "North Dakota" },
    { code: "US-OH", name: "Ohio" },
    { code: "US-OK", name: "Oklahoma" },
    { code: "US-OR", name: "Oregon" },
    { code: "US-PA", name: "Pennsylvania" },
    { code: "US-RI", name: "Rhode Island" },
    { code: "US-SC", name: "South Carolina" },
    { code: "US-SD", name: "South Dakota" },
    { code: "US-TN", name: "Tennessee" },
    { code: "US-TX", name: "Texas" },
    { code: "US-UT", name: "Utah" },
    { code: "US-VT", name: "Vermont" },
    { code: "US-VA", name: "Virginia" },
    { code: "US-WA", name: "Washington" },
    { code: "US-WV", name: "West Virginia" },
    { code: "US-WI", name: "Wisconsin" },
    { code: "US-WY", name: "Wyoming" },
    { code: "US-AS", name: "American Samoa" },
    { code: "US-GU", name: "Guam" },
    { code: "US-MP", name: "Northern Mariana Islands" },
    { code: "US-PR", name: "Puerto Rico" },
    { code: "US-VI", name: "U.S. Virgin Islands" },
  ],
  CA: [
    { code: "CA-AB", name: "Alberta" },
    { code: "CA-BC", name: "British Columbia" },
    { code: "CA-MB", name: "Manitoba" },
    { code: "CA-NB", name: "New Brunswick" },
    { code: "CA-NL", name: "Newfoundland and Labrador" },
    { code: "CA-NS", name: "Nova Scotia" },
    { code: "CA-NT", name: "Northwest Territories" },
    { code: "CA-NU", name: "Nunavut" },
    { code: "CA-ON", name: "Ontario" },
    { code: "CA-PE", name: "Prince Edward Island" },
    { code: "CA-QC", name: "Quebec" },
    { code: "CA-SK", name: "Saskatchewan" },
    { code: "CA-YT", name: "Yukon" },
  ],
  GB: [
    { code: "GB-ENG", name: "England" },
    { code: "GB-SCT", name: "Scotland" },
    { code: "GB-WLS", name: "Wales" },
    { code: "GB-NIR", name: "Northern Ireland" },
  ],
  AU: [
    { code: "AU-ACT", name: "Australian Capital Territory" },
    { code: "AU-NSW", name: "New South Wales" },
    { code: "AU-NT", name: "Northern Territory" },
    { code: "AU-QLD", name: "Queensland" },
    { code: "AU-SA", name: "South Australia" },
    { code: "AU-TAS", name: "Tasmania" },
    { code: "AU-VIC", name: "Victoria" },
    { code: "AU-WA", name: "Western Australia" },
  ],
};

/**
 * True if the given ISO 3166-1 alpha-2 code has a first-class subdivision
 * dropdown. All other countries accept validated free-text.
 */
export function hasSubdivisionData(countryCode: string): boolean {
  return Object.prototype.hasOwnProperty.call(SUBDIVISIONS, countryCode);
}

/**
 * Free-text subdivision validator for countries without a dropdown list.
 * Accepts 1-60 characters, letters (incl. accents), spaces, hyphens,
 * apostrophes, and periods. Rejects digits and symbols.
 *
 * This is intentionally strict so "Your Butt" fails ("B-u-t-t" fails only
 * because we check minimum 2 *letter* characters; actually "Your Butt"
 * passes this regex — see `isPlausibleSubdivisionText` below for the
 * real plausibility check).
 */
export function isValidSubdivisionText(s: string): boolean {
  const trimmed = s.trim();
  if (trimmed.length < 1 || trimmed.length > 60) return false;
  return /^[\p{L}][\p{L}\s\-'.]*$/u.test(trimmed);
}

/**
 * Plausibility check beyond pure syntax: reject obvious garbage like
 * "Your Face" / "Your Butt" by denying a small blocklist of common
 * test-entry phrases. Not a substitute for server-side checks, but
 * catches the most common abuse patterns.
 */
const SUBDIVISION_BLOCKLIST = new Set([
  "your face", "your butt", "my face", "my butt",
  "test", "testing", "asdf", "qwerty", "na", "n/a",
  "none", "nowhere", "somewhere", "earth", "mars",
]);

export function isPlausibleSubdivisionText(s: string): boolean {
  if (!isValidSubdivisionText(s)) return false;
  return !SUBDIVISION_BLOCKLIST.has(s.trim().toLowerCase());
}

/**
 * Lookup helpers
 */
export function getCountryByCode(code: string): Country | undefined {
  return COUNTRIES.find((c) => c.code === code);
}

export function getSubdivisionByCode(countryCode: string, subCode: string): Subdivision | undefined {
  return SUBDIVISIONS[countryCode]?.find((s) => s.code === subCode);
}
